import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useUserProfile } from '../../context/UserProfileContext';
import { fetchOrgInventoryImage } from '../../hooks/useOrgInventory';

interface InventoryItemImageProps {
  /** Org the item belongs to. Defaults to the active org. */
  orgId?: string;
  /** Any org inventory item (full or list shape). */
  item: { id: string; imageBase64?: string; hasImage?: boolean };
  alt: string;
  /** Classes for the <img> itself. */
  className?: string;
  /** Classes for the wrapper that also serves as the lazy-load sentinel. */
  wrapperClassName?: string;
  /** Rendered while the image loads, or when the item has none. */
  fallback: ReactNode;
}

/**
 * Thumbnail for an org inventory item.
 *
 * The inventory list endpoint omits base64 images to keep its payload small
 * and sets `hasImage` instead. This component shows `fallback` until the
 * wrapper scrolls near the viewport, then fetches the image once (cached in
 * the hook module) and swaps it in. Items that already carry `imageBase64`
 * (single-item responses) render immediately.
 */
export function InventoryItemImage({
  orgId,
  item,
  alt,
  className,
  wrapperClassName,
  fallback,
}: InventoryItemImageProps) {
  const { activeOrg } = useUserProfile();
  const resolvedOrgId = orgId ?? activeOrg?.orgId ?? '';
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState<string | null>(item.imageBase64 ?? null);

  // Observe visibility so a long list doesn't fire hundreds of image requests at once.
  useEffect(() => {
    if (item.imageBase64 || !item.hasImage || visible) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [item.imageBase64, item.hasImage, visible]);

  useEffect(() => {
    if (item.imageBase64) {
      setSrc(item.imageBase64);
      return;
    }
    if (!item.hasImage || !resolvedOrgId || !visible) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    fetchOrgInventoryImage(resolvedOrgId, item.id)
      .then(img => { if (!cancelled) setSrc(img); })
      .catch(() => { /* keep the fallback */ });
    return () => { cancelled = true; };
  }, [resolvedOrgId, item.id, item.imageBase64, item.hasImage, visible]);

  return (
    <div ref={ref} className={wrapperClassName}>
      {src ? <img src={src} alt={alt} className={className} /> : fallback}
    </div>
  );
}
